import type { OrderedSourceSnapshot } from './ordered-source';
import { orderHolds } from './site-games';

/**
 * The info point beside a Mexican-market site's game lists: the platform's initial order of their games was
 * modelled on these sites. Pure helpers only.
 */

/** The sites the initial order was modelled on, named as each brand writes its name (no translation changes it). */
export const INSPIRATION_SITES = [
  'Betway',
  'Jugabet',
  'Betcris',
  'Betsson',
  'Caliente',
  'Codere',
  'PlayCity',
  'Playdoit',
  'PlayUZU',
  'Strendus',
] as const;

/** Whether a page shows the Mexican market's lists: the service marks it so when its source (or the navigation that
 *  links it) is one an override record of the MEX region put on the site, the one way those lists reach a site. Never
 *  read from the site's language or country, which say nothing of the lists it shows (TalismanMax reads mx and names
 *  MEX, and its home page shows no Mexican list). */
export const showsMexicanLists = (page: { market?: string } | undefined): boolean => page?.market === 'MEX';

export type InitialOrderInput = {
  /** The site's lists on the page. Never the owner's own: those have no initial order of the platform's. */
  listIds: string[];
  /** Orders the owner changed in this session, by list. */
  drafts: Record<string, string[]>;
  /** Each list's order as the site shows it, for the lists the site sent. */
  siteOrders: Record<string, string[]>;
  /** What the service holds for each list it answered for. */
  snapshots: Record<string, OrderedSourceSnapshot>;
  /** The lists whose state the service could not give. */
  unread: ReadonlySet<string>;
};

/** `pending` while the service has yet to answer for a list; `unknown` when it could not, for one. */
export type InitialOrderVerdict = 'original' | 'changed' | 'pending' | 'unknown';

/**
 * Whether every site list on a page still shows its games in the platform's initial order. A list has left it once
 * an order of the brand's is live on the site (its override exists, `canRevert`), or while an order worked on (a
 * change made in this session, else the draft the service saved) differs from what the site shows (the order it
 * sent, else the service's live order). One list that left it is enough; else a list not read means no verdict.
 */
export const initialOrderVerdict = ({
  listIds,
  drafts,
  siteOrders,
  snapshots,
  unread,
}: InitialOrderInput): InitialOrderVerdict => {
  let unanswered: 'pending' | 'unknown' | null = null;
  for (const listId of listIds) {
    const saved = snapshots[listId];
    if (saved && (saved.canRevert || saved.published.length > 0)) return 'changed';
    const working = (drafts[listId]?.length ? drafts[listId] : null) ?? (saved?.draft?.length ? saved.draft : null);
    if (working) {
      const shown = siteOrders[listId] ?? (saved?.live.length ? saved.live : null);
      if (!shown || !orderHolds(working, shown)) return 'changed';
    }
    if (!saved && unanswered !== 'pending') unanswered = unread.has(listId) ? 'unknown' : 'pending';
  }
  return unanswered ?? 'original';
};
